/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Table, Spin, Alert, Button, message } from "antd";
import { onShowSizeChange, itemRender } from '../Pagination';
import Header from '../Header';
import Sidebar from '../Sidebar';
import { searchnormal, plusicon, refreshicon } from '../imagepath';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import doctorApi from '../../api/doctor.api';

const DoctorList = () => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [doctorToDelete, setDoctorToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const onSelectChange = (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
      };
      const onChange = (date, dateString) => {
        // console.log(date, dateString);
      };

    const fetchDoctors = async () => {
        try {
            setLoading(true);
            const response = await doctorApi.getAllDoctors();
            setDoctors(response);
            setError(null);
        } catch (err) {
            console.error('Error fetching doctors:', err);
            setError('Неуспешно зареждане на лекари. Моля опитайте отново.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDoctors();
    }, []);

    useEffect(() => {
        // Handle dropdown positioning
        const handleDropdownShow = (event) => {
            const trigger = event.target.closest('.dropdown-toggle');
            const menu = event.target.closest('.dropdown-action')?.querySelector('.dropdown-menu');
            
            if (trigger && menu) {
                const triggerRect = trigger.getBoundingClientRect();
                const menuHeight = 200; // Approximate menu height
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - triggerRect.bottom;
                const spaceAbove = triggerRect.top;
                
                // Determine if menu should open up or down
                if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
                    // Open upward
                    menu.style.top = `${triggerRect.top - menuHeight - 5}px`;
                    menu.style.left = `${triggerRect.right - 150}px`;
                } else {
                    // Open downward
                    menu.style.top = `${triggerRect.bottom + 5}px`;
                    menu.style.left = `${triggerRect.right - 150}px`;
                }
            }
        };

        // Add event listeners for Bootstrap dropdown events
        document.addEventListener('show.bs.dropdown', handleDropdownShow);
        
        return () => {
            document.removeEventListener('show.bs.dropdown', handleDropdownShow);
        };
    }, []);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleDeleteClick = (doctor) => {
        setDoctorToDelete(doctor);
    };

    const handleDeactivateConfirm = async () => {
        if (!doctorToDelete) return;
        
        try {
            setDeleting(true);
            await doctorApi.deleteDoctor(doctorToDelete.id);
            
            // Remove from local state or refetch
            setDoctors(prev => prev.filter(d => d.id !== doctorToDelete.id));
            
            // Show success message
            message.success(`Лекарят ${doctorToDelete.fullName || doctorToDelete.Name} беше деактивиран успешно`);
            
            setDoctorToDelete(null);
            
        } catch (err) {
            console.error('Error deactivating doctor:', err);
            message.error('Грешка при деактивирането на лекаря. Моля опитайте отново.');
        } finally {
            setDeleting(false);
        }
    };

    const handlePermanentDeleteConfirm = async () => {
        if (!doctorToDelete) return;
        
        try {
            setDeleting(true);
            await doctorApi.permanentlyDeleteDoctor(doctorToDelete.id);
            
            // Remove from local state or refetch
            setDoctors(prev => prev.filter(d => d.id !== doctorToDelete.id));
            
            // Show success message
            message.success(`Лекарят ${doctorToDelete.fullName || doctorToDelete.Name} беше изтрит окончателно`);
            
            setDoctorToDelete(null);
            
        } catch (err) {
            console.error('Error permanently deleting doctor:', err);
            message.error('Грешка при окончателното изтриване на лекаря. Моля опитайте отново.');
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDoctorToDelete(null);
    };


    const filteredDoctors = doctors.filter(doctor =>
        doctor.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doctor.uinNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doctor.speciality?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const calculateExperience = (joiningDate) => {
        if (!joiningDate) return 0;
        const joining = new Date(joiningDate);
        const today = new Date();
        const years = today.getFullYear() - joining.getFullYear();
        return years > 0 ? years : 0;
    };

    const datasource = filteredDoctors.filter(doctor => doctor && doctor.id).map(doctor => ({
        id: doctor.id,
        key: doctor.id, // Ensure each row has a unique key
        Name: doctor.fullName || `${doctor.firstName} ${doctor.lastName}`,
        UinNumber: doctor.uinNumber,
        Specialties: doctor.speciality ? [doctor.speciality] : ['Неопределена'], // Convert to array format
        LicenseNumber: doctor.uinNumber, // Using UIN as license for now
        YearsExperience: calculateExperience(doctor.joiningDate),
        WorkingHours: '08:00-17:00', // Default working hours
        IsSubstitute: false, // Default to regular doctor
        PatientCount: 0, // This would come from patient count API
        department: doctor.department,
        active: doctor.active,
        ...doctor
    }));

    const columns = [
        {
            title: "Име",
            dataIndex: "Name",
            render: (text, record) => (
                <Link to={`/doctors/medical-profile/${record.id}`} className="text-decoration-none" title={record.Name}>
                    {record.Name}
                </Link>
            ),
            sorter: (a, b) => a.Name.length - b.Name.length,
            width: "30%"
        },
        {
            title: "УИН",
            dataIndex: "UinNumber",
            render: (text, record) => (
                <span className="badge bg-primary">{record.UinNumber}</span>
            ),
            sorter: (a, b) => a.UinNumber.localeCompare(b.UinNumber),
            width: "15%"
        },
        {
            title: "Специалности",
            dataIndex: "Specialties",
            render: (specialties, record) => (
                <div title={specialties.join(', ')}>
                    {specialties.slice(0, 2).map((specialty, index) => (
                        <span key={index} className="badge bg-success me-1 mb-1">
                            {specialty}
                        </span>
                    ))}
                    {specialties.length > 2 && (
                        <span className="badge bg-secondary">+{specialties.length - 2}</span>
                    )}
                </div>
            ),
            sorter: (a, b) => a.Specialties.length - b.Specialties.length,
            width: "25%"
        },
        {
            title: "Статус",
            dataIndex: "active",
            render: (active, record) => (
                <span className={`badge ${active ? 'bg-success' : 'bg-danger'}`}>
                    {active ? 'Активен' : 'Неактивен'}
                </span>
            ),
            sorter: (a, b) => a.active - b.active,
            width: "15%"
        },
        {
            title: "Пациенти",
            dataIndex: "PatientCount",
            render: (count, record) => (
                <span className="badge bg-secondary">{count}</span>
            ),
            sorter: (a, b) => a.PatientCount - b.PatientCount,
            width: "10%"
        },
        {
          title: "",
          dataIndex: "FIELD8",
          render: (text, record) => (
            <>
              <div className="text-end">
                <div className="dropdown dropdown-action">
                  <Link
                    to="#"
                    className="action-icon dropdown-toggle"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <FeatherIcon icon="more-vertical" />
                  </Link>
                  <div className="dropdown-menu dropdown-menu-end">
                    <Link className="dropdown-item" to={`/doctors/edit/${record.id}`}>
                      <i className="far fa-edit me-2" />
                      Редактирай
                    </Link>
                    <Link className="dropdown-item" to={`/doctors/medical-profile/${record.id}`}>
                      <i className="far fa-user me-2" />
                      Профил
                    </Link>
                    <Link
                      className="dropdown-item"
                      to="#"
                      data-bs-toggle="modal"
                      data-bs-target="#delete_doctor"
                      onClick={() => handleDeleteClick(record)}
                    >
                      <i className="far fa-trash-alt me-2" />
                      Изтрий
                    </Link>
                  </div>
                </div>
              </div>
            </>
          )
        }
    ]
    const hasSelected = selectedRowKeys.length > 0;

  return (
    <>
    <Header/>
    <Sidebar id="menu-item1" id1="menu-items1" activeClassName="doctor-list"/>
    <>
  <div className="page-wrapper">
    <div className="content">
      {/* Page Header */}
      <div className="page-header">
        <div className="row">
          <div className="col-12 col-sm-7">
            <h3 className="page-title">Списък лекари</h3>
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/admin-dashboard">Лекари</Link>
              </li>
              <li className="breadcrumb-item">
                <i className="feather-chevron-right">
                  <FeatherIcon icon="chevron-right" />
                </i>
              </li>
              <li className="breadcrumb-item active">Списък лекари</li>
            </ul>
          </div>
        </div>
      </div>
      {/* /Page Header */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card card-table show-entire">
            <div className="card-body">
              {error && (
                <Alert
                  message="Грешка"
                  description={error}
                  type="error"
                  showIcon
                  closable
                  className="mb-3"
                  action={(
                    <Button size="small" type="primary" onClick={fetchDoctors}>
                      Опитай отново
                    </Button>
                  )}
                />
              )}
              {/* Table Header */}
              <div className="page-table-header mb-2">
                <div className="row align-items-center">
                  <div className="col">
                    <div className="doctor-table-blk" style={{ maxWidth: '100%', overflow: 'hidden' }}>
                      <h3>Списък лекари</h3>
                      <div className="doctor-search-blk" style={{ maxWidth: '100%' }}>
                        <div className="top-nav-search table-search-blk" style={{ maxWidth: '400px' }}>
                          <form>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Търси тук"
                              value={searchTerm}
                              onChange={handleSearch}
                              style={{ maxWidth: '350px' }}
                            />
                            <Link className="btn">
                              <img src={searchnormal} alt="" />
                            </Link>
                          </form>
                        </div>
                        <div className="add-group">
                          <Link to="/doctors/add" className="btn btn-primary add-pluss ms-2">
                            <img src={plusicon} alt="Добави" />
                          </Link>
                          <Link to="#" onClick={fetchDoctors} className="btn btn-primary doctor-refresh ms-2">
                            <img src={refreshicon} alt="Обнови" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* /Table Header */}
              <div className="table-responsive doctor-list">
                <Spin spinning={loading}>
                <Table
                  pagination={false}
                  columns={columns}
                  dataSource={datasource}
                  rowSelection={rowSelection}
                  rowKey="key"
                  scroll={{ x: 1200 }}
                  locale={{
                    emptyText: loading ? 'Зареждане...' : 'Няма данни'
                  }}
                />
                </Spin>
              </div>

              <style>{`
                .ant-table-thead > tr > th {
                  white-space: nowrap !important;
                  padding: 8px 8px !important;
                  font-weight: 600 !important;
                }
                .ant-table-tbody > tr > td {
                  white-space: nowrap !important;
                  padding: 8px 8px !important;
                }
                .table-responsive {
                  overflow-x: auto !important;
                  overflow-y: hidden !important;
                }
                .doctor-list .ant-table {
                  min-width: 1200px;
                  width: 100%;
                }
                .doctor-list .ant-table-tbody > tr > td,
                .doctor-list .ant-table-thead > tr > th {
                  min-width: 80px;
                }
                
                /* Hide Ant Design's measure row */
                .ant-table-measure-row {
                  display: none !important;
                }
                
                /* Responsive table behavior */
                .table-responsive {
                  overflow-x: auto !important;
                  overflow-y: hidden !important;
                }
                .card-body {
                  overflow-x: auto !important;
                }
                
                /* Desktop: Allow dropdowns to escape table bounds */
                @media (min-width: 769px) {
                  .table-responsive {
                    overflow-y: visible !important;
                  }
                  .card-body {
                    overflow: visible !important;
                  }
                }
                
                /* Dropdown positioning for all devices */
                .dropdown-action .dropdown-menu {
                  position: fixed !important;
                  z-index: 1060 !important;
                  min-width: 150px !important;
                  max-height: none !important;
                  overflow: visible !important;
                }
                
                .dropdown-action.show .dropdown-menu {
                  display: block !important;
                }
                
                  
                  /* Force pagination right alignment */
                  .ant-table-pagination-right {
                    text-align: right !important;
                    display: flex !important;
                    justify-content: flex-end !important;
                    padding-right: 20px !important;
                  }
                  
                  
                  /* Mobile specific left alignment */
                  @media (max-width: 768px) {
                    .ant-table-pagination-right,
                    .ant-pagination {
                      text-align: left !important;
                      display: flex !important;
                      justify-content: flex-start !important;
                      width: 100% !important;
                      margin: 0 !important;
                      padding-left: 20px !important;
                    }
                  }
                  
                  .doctor-search-blk {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                    flex-wrap: wrap !important;
                  }
                  
                  .add-group {
                    display: flex !important;
                    align-items: center !important;
                  }
                  
                  @media (min-width: 769px) {
                    .doctor-search-blk {
                      flex-wrap: nowrap !important;
                    }
                    .doctor-search-blk .top-nav-search {
                      margin-bottom: 0 !important;
                      margin-right: 15px !important;
                    }
                  }
                  
                  @media (max-width: 768px) {
                    .doctor-search-blk {
                      flex-direction: column !important;
                      align-items: flex-start !important;
                    }
                    .doctor-search-blk .top-nav-search {
                      margin-bottom: 10px !important;
                      width: 200px !important;
                      max-width: 60vw !important;
                    }
                    .add-group {
                      display: none !important;
                    }
                  }
                  .doctor-search-blk .top-nav-search .form-control {
                    width: 100% !important;
                    font-size: 14px !important;
                  }
                  .doctor-table-blk {
                    overflow: hidden !important;
                  }
                  .page-table-header {
                    overflow: hidden !important;
                  }
                }
              `}</style>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  {/* Delete Doctor Modal */}
  <div className="modal fade" id="delete_doctor" aria-hidden="true" role="dialog">
    <div className="modal-dialog modal-dialog-centered" role="document">
      <div className="modal-content">
        <div className="modal-body">
          <div className="form-content p-3">
            <h4 className="modal-title mb-3">Изтриване на лекар</h4>
            <p className="mb-4">
              {doctorToDelete ? 
                `Как искате да премахнете ${doctorToDelete.fullName || doctorToDelete.Name}?` :
                'Как искате да премахнете този лекар?'
              }
            </p>
            
            {/* Deactivate Option */}
            <div className="border rounded p-3 mb-3">
              <h6 className="text-warning mb-2">
                <i className="fas fa-pause-circle me-2"></i>
                Деактивиране (препоръчително)
              </h6>
              <p className="small text-muted mb-3">
                Лекарят ще бъде скрит от активните списъци, но всички данни и история ще останат запазени. Може да бъде активиран отново в бъдеще.
              </p>
              <button
                type="button"
                className="btn btn-warning w-100"
                data-bs-dismiss="modal"
                onClick={handleDeactivateConfirm}
                disabled={deleting}
              >
                {deleting ? 'Деактивира...' : 'Деактивирай лекаря'}
              </button>
            </div>

            {/* Permanent Delete Option */}
            <div className="border border-danger rounded p-3 mb-3">
              <h6 className="text-danger mb-2">
                <i className="fas fa-trash-alt me-2"></i>
                Окончателно изтриване
              </h6>
              <p className="small text-muted mb-3">
                <strong className="text-danger">ВНИМАНИЕ:</strong> Всички данни за лекаря ще бъдат окончателно изтрити и няма да могат да бъдат възстановени. Използвайте само ако сте сигурни!
              </p>
              <button
                type="button"
                className="btn btn-danger w-100"
                data-bs-dismiss="modal"
                onClick={handlePermanentDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? 'Изтрива окончателно...' : 'Изтрий окончателно'}
              </button>
            </div>

            {/* Cancel Button */}
            <button
              type="button"
              className="btn btn-secondary w-100"
              data-bs-dismiss="modal"
              onClick={handleDeleteCancel}
            >
              Отказ
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</>
</>
  )
}

export default DoctorList